import { useCallback, useEffect, useState } from "react";
import companyApi from "../../../api/company";
import configApi from "../../../api/configuration";
import marginsApi from "../../../api/margins";
import productsApi from "../../../api/products";
import { useApi } from "../../../hooks/useApi";
import { useUserContext } from "../../../hooks/useUserContext";
import { filterObjectByKeys } from "../../../utilities/objectUtilities";
import { LoadingIndicator } from "../../LoadingIndicator";
import { Commodity, ICommodity, IInstrument, Instrument } from "../../quotes";
import { HeaderTextSelector } from "../../utilities";
import { Company, ICompany } from "../Company";
import { Configuration, IConfiguration } from "./Configuration";
import { ConfigurationsOverviewTab, ConfigurationsWorkflow } from ".";

const OVERVIEW_VIEW = "Overview";
const BULK_VIEW = "Bulk Operations";

export const ProductConfigurations = () => {
  const getBusinessTypesApi = useApi(productsApi.getBusinessTypesByCompany);
  const getCompaniesApi = useApi(companyApi.getLineage);
  const getConfigurationsApi = useApi(configApi.getConfigurations);
  const getCommoditiesApi = useApi(productsApi.getCommodities);
  const getCommodityTypesApi = useApi(productsApi.getCommodityTypes);
  const getInstrumentsApi = useApi(productsApi.getInstruments);
  const getMarginsApi = useApi(marginsApi.getMargins);
  const getProductTypesApi = useApi(productsApi.getProductTypes);

  const [loading, setLoading] = useState(false);
  const [selectedView, setSelectedView] = useState(OVERVIEW_VIEW);
  const [businessTypes, setBusinessTypes] = useState<Record<number, string>>({});
  const [companies, setCompanies] = useState<Company[]>([]);
  const [commodities, setCommodities] = useState<Commodity[]>([]);
  const [commodityTypes, setCommodityTypes] = useState<Record<string, string>>({});
  const [configurations, setConfigurations] = useState<Record<number, Configuration[]>>({});
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [margins, setMargins] = useState<any[]>([]);
  const [productTypes, setProductTypes] = useState<Record<string, string>>({});

  const { context: user } = useUserContext();
  const companyId = user.activeCompany.id;
  const marginField = user.roles.isRiskTaker
    ? "riskTakerMarginId"
    : user.roles.isBroker
      ? "brokerMarginId"
      : "";
  const childMarginField = user.roles.isRiskTaker ? "brokerMarginId" : undefined;

  useEffect(() => {
    const getLists = async () =>
      await Promise.all([
        getBusinessTypesApi.request(companyId),
        getCompaniesApi.request(),
        getProductTypesApi.request(),
        getCommodityTypesApi.request(),
        getCommoditiesApi.request(),
        getInstrumentsApi.request(0),
        getMarginsApi.request(user.roles.isRiskTaker),
      ]);

    setLoading(true);
    getLists();
    getConfigurations();
  }, [companyId]);

  const getConfigurations = useCallback(async () => {
    getConfigurationsApi.request();
  }, [getConfigurationsApi]);

  const getMargins = useCallback(async () => {
    getMarginsApi.request(user.roles.isRiskTaker);
  }, [getMarginsApi]);

  useEffect(() => {
    if (getBusinessTypesApi.data) setBusinessTypes(getBusinessTypesApi.data.businessTypes);
  }, [getBusinessTypesApi.data]);

  useEffect(() => {
    if (getCompaniesApi.data)
      setCompanies(getCompaniesApi.data.companies.map((c: ICompany) => Company.create(c)) || []);
  }, [getCompaniesApi.data]);

  useEffect(() => {
    if (getCommoditiesApi.data)
      setCommodities(
        (getCommoditiesApi.data.commodities || []).map((c: ICommodity) => Commodity.create(c)),
      );
  }, [getCommoditiesApi.data]);

  useEffect(() => {
    if (getInstrumentsApi.data)
      setInstruments(
        (getInstrumentsApi.data.instruments || []).map((c: IInstrument) => Instrument.create(c)),
      );
  }, [getInstrumentsApi.data]);

  useEffect(() => {
    if (getMarginsApi.data) setMargins(getMarginsApi.data.margins || []);
  }, [getMarginsApi.data]);

  useEffect(() => {
    setLoading(getConfigurationsApi.loading);

    if (getConfigurationsApi.data) {
      const configs = (getConfigurationsApi.data.configurations || []).map((c: IConfiguration) => {
        const company = companies.find((com) => com.id === c.companyId);
        const commodity = commodities.find((com) => com.id === c.commodityId);
        const instrument = instruments.find(
          (i) => i.instrumentType === c.instrumentType && i.productType === c.productType,
        );

        const config = Configuration.create(c, {
          company: company?.name,
          commodityIcon: commodity?.icon(),
          commodity: commodity?.name,
          commodityType: commodity?.type,
          instrument: instrument?.name,
          typeIcon: instrument?.icon(),
          type: productTypes[c.productType],
        });

        return config;
      });

      const configItems: Record<number, Configuration[]> = configs.reduce(
        (acc: any, cur: Configuration) => {
          const key = cur.companyId;
          if (acc[key]) acc[key].push(cur);
          else acc[key] = [cur];
          return acc;
        },
        {},
      );

      setConfigurations(configItems);
    }
  }, [
    getConfigurationsApi.data,
    getConfigurationsApi.loading,
    companies,
    commodities,
    instruments,
  ]);

  useEffect(() => {
    const companyConfigs = configurations[companyId];
    if (
      companyConfigs &&
      getCommodityTypesApi.data?.commodityTypes &&
      getProductTypesApi.data?.productTypes
    ) {
      const companyCommodityTypes = new Set<string>(companyConfigs.map((c) => c.commodityType));
      const companyProductTypes = new Set<string>(companyConfigs.map((c) => c.productType));

      setCommodityTypes(
        filterObjectByKeys(getCommodityTypesApi.data.commodityTypes, [...companyCommodityTypes]),
      );
      setProductTypes(
        filterObjectByKeys(getProductTypesApi.data.productTypes, [...companyProductTypes]),
      );
    }
  }, [configurations, getCommodityTypesApi.data, getProductTypesApi.data]);

  const handleBulkOperationComplete = () => {
    setSelectedView(OVERVIEW_VIEW);
    getConfigurations();
  };

  return (
    <>
      <HeaderTextSelector
        values={[OVERVIEW_VIEW, BULK_VIEW]}
        selected={selectedView}
        onChange={setSelectedView}
      />

      <LoadingIndicator loading={loading} />

      {selectedView === OVERVIEW_VIEW && !loading && (
        <ConfigurationsOverviewTab
          commodityTypes={commodityTypes}
          companies={companies}
          companyId={companyId}
          configurations={configurations}
          margins={margins}
          marginField={marginField}
          childMarginField={childMarginField}
          productTypes={productTypes}
          onChange={getConfigurations}
        />
      )}

      {selectedView === BULK_VIEW && !loading && (
        <ConfigurationsWorkflow
          businessTypes={businessTypes}
          commodities={commodities}
          commodityTypes={commodityTypes}
          companies={companies}
          companyId={companyId}
          configurations={configurations}
          margins={margins}
          marginField={marginField}
          productTypes={productTypes}
          onComplete={handleBulkOperationComplete}
          onMarginsUpdate={getMargins}
        />
      )}
    </>
  );
};
